import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import { CustomerIdentityKpis } from "../app/components/domain/customers/CustomerIdentityKpis";
import { CustomerFirmographicSummary } from "../app/components/domain/customers/CustomerFirmographicSummary";
import { CustomerStrategyPyramid } from "../app/components/domain/customers/CustomerStrategyPyramid";
import { CustomerGroupCard } from "../app/components/domain/customers/CustomerGroupCard";
import { CustomerImprovementLogs } from "../app/components/domain/customers/CustomerImprovementLogs";
import { CustomerCampaignHistory } from "../app/components/domain/customers/CustomerCampaignHistory";
import { CustomerContacts } from "../app/components/domain/customers/CustomerContacts";
import { CustomerTabs } from "../app/components/domain/customers/CustomerTabs";
import { useCustomerRouteId } from "../app/components/domain/customers/useCustomerRouteId";
import { CustomerPortalSummary } from "../app/components/domain/customers/CustomerPortalSummary";
import { SegmentBadge } from "../app/components/domain/customers/SegmentBadge";
import { CreditChip } from "../app/components/domain/credit-control/CreditChip";
import { TicketModal } from "../app/components/domain/tickets/TicketModal";
import {
  AlertTriangle,
  ChevronLeft,
  Eye,
  Plus,
  RefreshCw,
  Star,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import { Skeleton } from "../app/components/primitives/skeleton";
import { isFeatureEnabled } from "@shared-core/config/features";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { useCanViewCustomerPortalAs } from "../app/auth/hooks";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { ApiError } from "@shared-core/data/api/errors";
import { VisibilityProvenanceBadge } from "../app/components/domain/account-allocation/VisibilityProvenanceBadge";

/**
 * CSR account allocation (docs/features/csr-account-allocation/) — the
 * responsible-CSR line + Claim action on customer-360. Everything shown comes
 * from the composed response's `allocation` block; the page resolves nothing.
 */
function describeUnallocatedCsr(
  allocation: API.CustomerAllocation | null | undefined,
): string {
  const raw = allocation?.raw_ref2?.trim() ?? "";
  switch (allocation?.allocation_reason) {
    case "blank":
      return "no CSR recorded in Tharstern";
    case "left":
      return `${raw || "the previous CSR"} has left`;
    case "bucket":
    case "unrecognised":
      return `Tharstern shows "${raw}"`;
    default:
      return "no CSR recorded";
  }
}

export function CustomerDetailPage() {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const canPreviewPortal = useCanViewCustomerPortalAs();
  // The page is keyed by the customer's CIS code; panels by its Tharstern code (v1 completion C3).
  const customerId = useCustomerRouteId() ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  // location.key is "default" only on a fresh/direct load (deep link, page
  // refresh) — anything else means we arrived via in-app navigation, so
  // going back preserves the referring page's filters/search/scope/page
  // (its state lives in that page's own URL query string, which browser
  // history restores). Falls back to the plain customers link otherwise,
  // since there's nothing in-app to go back to.
  const canGoBackToCustomers = location.key !== "default";
  const handleBackToCustomersClick = (e: React.MouseEvent) => {
    if (canGoBackToCustomers) {
      e.preventDefault();
      navigate(-1);
    }
  };
  const [customer, setCustomer] =
    useState<API.CustomerIntelligenceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Separate from `customer === null`: a failed request and a customer that
  // genuinely does not exist need different words and different recovery.
  // Collapsing them told everyone whose request timed out that the account
  // they were looking at had been deleted.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState<string | null>(null);

  // Ticket creation modal state
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);

  // CSR account allocation — the responsible CSR and this viewer's claim
  // state arrive on the customer-360 response (`customer.allocation`).
  // `claimedNow` only carries the claim made on this page since load, so the
  // button flips without refetching the whole view.
  const [claimedNow, setClaimedNow] =
    useState<API.CustomerAllocation["viewer_claim"]>(null);
  const [claiming, setClaiming] = useState(false);

  async function handleClaim() {
    if (!customerId || claiming) return;
    setClaiming(true);
    try {
      const { claim } = await dataAdapter.claimCustomer(customerId);
      setClaimedNow({
        claim_id: claim.claim_id,
        status: claim.status,
        note: claim.note,
      });
      toast.success("Claim sent to your manager");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.detail : "Could not claim this account",
      );
    } finally {
      setClaiming(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      if (!customerId) return;

      setLoading(true);
      setLoadError(null);
      setClaimedNow(null);
      try {
        const customerData = await dataAdapter.getCustomerById(customerId);
        if (cancelled) return;
        setCustomer(customerData ?? null);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading customer data:", error);
        setLoadError(error instanceof Error ? error.message : String(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on customer id; adapter identity is stable in-app
  }, [customerId]);

  const backToCustomersLink = (
    <Link
      to={paths.customers()}
      onClick={handleBackToCustomersClick}
      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" />
      Back to Customers
    </Link>
  );

  // Skeleton rather than a "Loading..." line: the page is tall and card-shaped,
  // so holding its shape keeps the layout from jumping when data lands.
  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading customer…</span>
        {backToCustomersLink}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-4 w-full max-w-2xl" />
          <Skeleton className="h-4 w-full max-w-md" />
          <div className="flex gap-2 pt-3">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
        <Skeleton className="h-28 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        {backToCustomersLink}
        <div className="border border-border rounded-lg p-8 text-center space-y-3">
          <AlertTriangle className="size-6 mx-auto text-amber-500" />
          <div>
            <p className="font-medium">Couldn't load this customer</p>
            <p className="text-sm text-muted-foreground mt-1">{loadError}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(0)}>
            <RefreshCw className="size-4" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-4">
        {backToCustomersLink}
        <div className="border border-border rounded-lg p-8 text-center space-y-1">
          <p className="font-medium">Customer not found</p>
          <p className="text-sm text-muted-foreground">
            No customer matches{" "}
            <span className="font-medium">{customerId}</span>. It may have been
            merged into another account.
          </p>
        </div>
      </div>
    );
  }

  // Customer 360 returns owning-module alert/opportunity rows; this page
  // only counts the open ones (the work surface renders them elsewhere).
  const alerts = (customer.alerts ?? []).filter((a) => a.status === "open");
  const actions = (customer.opportunities ?? []).filter(
    (a) => a.status === "open",
  );
  const notes = customer.notes ?? [];
  const contacts = customer.contacts ?? [];
  // Rep-set "key decision maker" flag — contact-key-decision-maker-flag.
  // Sourced from the already-loaded customer-360 contacts (no extra fetch);
  // CustomerContacts renders its own separately-fetched Tharstern contact
  // list (/views/contacts) and cross-references this id set to show the
  // same flag without duplicating the read.
  const keyPersonContactIds = new Set(
    contacts.filter((c) => c.is_key_decision_maker).map((c) => c.id),
  );
  const keyPersonContacts = contacts.filter((c) => c.is_key_decision_maker);
  // The health tier override and key person controls are offered to a person
  // who may run the record's commands (Override health tier…, Clear health tier
  // override, Set key person…). Presence is what counts, not `enabled`: whether
  // an override exists is this page's own state, which it updates as it goes.
  const offersSetting = (command: string) =>
    (customer.setting_commands ?? []).some((c) => c.name === command);
  const hasEnrichment = Boolean(
    customer.description ||
    customer.uk_turnover_gbp != null ||
    customer.uk_employees != null ||
    customer.sector,
  );
  // Authoritative "has a run completed" signal — decoupled from whether any
  // particular field returned a value. Drives the "not yet enriched" vs
  // "enriched but no info found" distinction in null-field tooltips.
  const isEnriched = Boolean(customer.last_enriched_at);

  // Derived KPI values. Live-estimate and live-order counts and totals come
  // from the daily-metrics rollup (authoritative customer-wide values) rather
  // than filtering a single page of the list response — the page filter
  // under-counts anyone above the default page size.
  const liveEstimateCount = customer.metrics?.count_open_estimates ?? 0;
  const liveEstimateValue = customer.metrics?.total_open_estimate_value ?? 0;
  // Live jobs (WIP) figures come from customer-360's is_wip-derived fields,
  // not the daily-metrics count_live_orders (which is invoice-axis and drifts).
  const liveJobCount = customer.live_jobs_count ?? 0;
  const liveJobValue = customer.live_jobs_value ?? 0;
  const openTicketCount = customer.open_ticket_count ?? 0;

  const handleReEnrich = async () => {
    if (!customerId || isEnriching) return;
    setIsEnriching(true);
    setEnrichError(null);
    try {
      const { runId } = await dataAdapter.triggerCustomerRefresh(customerId);
      // Poll until the run completes or fails.
      const poll = async (): Promise<void> => {
        const run = await dataAdapter.getRefreshRunStatus(runId);
        if (run.status === "RUNNING") {
          await new Promise((r) => setTimeout(r, 3000));
          return poll();
        }
        if (run.status === "FAILED") {
          throw new Error(run.failureReason ?? "Enrichment run failed.");
        }
        // Reload customer data now that enrichment is done.
        const refreshed = await dataAdapter.getCustomerById(customerId);
        if (refreshed) setCustomer(refreshed);
      };
      await poll();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[enrichment] refresh failed:", err);
      setEnrichError(message);
    } finally {
      setIsEnriching(false);
    }
  };

  const handleToggleKeyPerson = async (
    contactId: string,
    isKeyPerson: boolean,
  ) => {
    if (!customerId) return;
    // Optimistic update: the flag is a small rep-facing toggle, not a
    // multi-step workflow, so reflect it immediately rather than waiting on
    // a full customer-360 reload.
    setCustomer((prev) =>
      prev
        ? {
            ...prev,
            contacts: (prev.contacts ?? []).map((c) =>
              c.id === contactId
                ? { ...c, is_key_decision_maker: isKeyPerson }
                : c,
            ),
          }
        : prev,
    );
    try {
      await dataAdapter.setContactKeyPerson(contactId, customerId, isKeyPerson);
    } catch (error) {
      console.error("Error setting contact key-person flag:", error);
      // Revert on failure.
      setCustomer((prev) =>
        prev
          ? {
              ...prev,
              contacts: (prev.contacts ?? []).map((c) =>
                c.id === contactId
                  ? { ...c, is_key_decision_maker: !isKeyPerson }
                  : c,
              ),
            }
          : prev,
      );
    }
  };

  // Rep-set manual account-health tier override. Unlike the optimistic
  // key-person toggle, this is form-driven (tier + required reason +
  // required expiry) so the network call runs first and the caller
  // (AccountHealthCard's dialog) surfaces a thrown error inline rather than
  // the page silently reverting an optimistic update.
  const handleSetHealthOverride = async (
    overrideTier: API.HealthTierOverrideValue,
    reason: string,
    expiresAt: string,
  ) => {
    if (!customerId) return;
    const override = await dataAdapter.setCustomerHealthTierOverride(
      customerId,
      overrideTier,
      reason,
      expiresAt,
    );
    setCustomer((prev) =>
      prev ? { ...prev, health_tier_override: override } : prev,
    );
  };

  const handleClearHealthOverride = async () => {
    if (!customerId) return;
    const previous = customer?.health_tier_override ?? null;
    // Optimistic: clearing is a single click with nothing to validate, same
    // shape as handleToggleKeyPerson.
    setCustomer((prev) =>
      prev ? { ...prev, health_tier_override: null } : prev,
    );
    try {
      await dataAdapter.clearCustomerHealthTierOverride(customerId);
    } catch (error) {
      console.error("Error clearing customer health-tier override:", error);
      setCustomer((prev) =>
        prev ? { ...prev, health_tier_override: previous } : prev,
      );
    }
  };

  const handleCreateTicket = async (data: {
    title: string;
    description: string;
    actionType: "call" | "email" | "meeting" | "general";
    dueDate: string;
    priority: "low" | "normal" | "high" | "urgent";
    categoryId: string | null;
    customerId: string | null;
    assignedToUserId: string | null;
    customerPersonId: string | null;
    subjectType: "alert" | "opportunity" | "job" | "estimate" | null;
    subjectId: string | null;
    subjectLabel: string | null;
  }) => {
    if (!customerId) return;
    setIsSubmittingTicket(true);
    try {
      await dataAdapter.createTicket({
        title: data.title,
        description: data.description || undefined,
        due_date: data.dueDate || undefined,
        action_type: data.actionType,
        customer_id: data.customerId ?? customerId,
        priority: data.priority,
        category_id: data.categoryId ?? undefined,
        assigned_to_user_id: data.assignedToUserId ?? undefined,
        customer_person_id: data.customerPersonId ?? undefined,
        subject_type: data.subjectType ?? undefined,
        subject_id: data.subjectId ?? undefined,
        subject_label: data.subjectLabel ?? undefined,
      });
      setShowCreateTicket(false);
      // Reload customer-360 to pick up the updated open_ticket_count.
      const refreshed = await dataAdapter.getCustomerById(customerId);
      if (refreshed) setCustomer(refreshed);
    } catch (error) {
      console.error("Error creating ticket:", error);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      {backToCustomersLink}

      {/* ── Identity header ─────────────────────────────────────── */}
      <div className="space-y-3">
        {/* Row 1: Name + segment + account code, then the create actions.
            Only actions that START something live here — "go and look at
            X" moved to the tabs at the foot of this block,
            so the two kinds of control stopped competing for the same
            corner of the page. */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-wrap min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">
              {customer.customer.name}
            </h1>
            <SegmentBadge segment={customer.segment?.segment_id || ""} />
            <CreditChip customerId={customer.customer.id} />
            {customer.customer?.customer_code && (
              <span className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                {customer.customer.customer_code}
              </span>
            )}
            {isFeatureEnabled("v1_completion") &&
              customer.customer?.cis_customer_code && (
                <span className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {customer.customer.cis_customer_code}
                </span>
              )}
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
            {isRouteEnabled("createEstimate") &&
              isFeatureEnabled("merchandise_draft_estimates") && (
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={paths.createEstimate({
                      customerCode:
                        customer.customer?.customer_code || undefined,
                    })}
                  >
                    <Plus className="size-4" />
                    New merchandise request
                  </Link>
                </Button>
              )}
            <Button size="sm" onClick={() => setShowCreateTicket(true)}>
              <Plus className="size-4" />
              Create Ticket
            </Button>
            {canPreviewPortal && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  // The staff view is another bundle on this origin: a full
                  // navigation, same-tab, as the other portal entry points.
                  capabilities.redirectTo(
                    `/portal-view/customer/${encodeURIComponent(customer.customer.id)}/orders`,
                  )
                }
              >
                <Eye className="size-4" />
                View portal
              </Button>
            )}
          </div>
        </div>

        {/* Row 1.5: responsible CSR — product brief §8 J2/§11. */}
        {(() => {
          const allocation = customer.allocation;
          const viewerClaim = claimedNow ?? allocation?.viewer_claim ?? null;
          const provenance = (
            <VisibilityProvenanceBadge
              visibilityReason={customer.visibility_reason}
              coveringForUserName={customer.covering_for_user_name}
              coverUntil={customer.cover_until}
            />
          );
          if (allocation?.responsible_csr_name) {
            return (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  CSR:{" "}
                  <span className="text-foreground">
                    {allocation.responsible_csr_name}
                  </span>
                </p>
                {provenance}
              </div>
            );
          }
          return (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                CSR: none — {describeUnallocatedCsr(allocation)}
              </span>
              {provenance}
              {viewerClaim?.status === "pending" && (
                <Badge variant="warning">Waiting for approval</Badge>
              )}
              {viewerClaim?.status === "approved" && (
                <Badge variant="success">Approved</Badge>
              )}
              {viewerClaim?.status === "declined" && (
                <span className="text-xs text-destructive">
                  Declined
                  {viewerClaim.note ? `: ${viewerClaim.note}` : ""}
                </span>
              )}
              {allocation?.is_claimable &&
                viewerClaim?.status !== "pending" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClaim}
                    disabled={claiming}
                  >
                    {claiming ? "Claiming…" : "Claim this account"}
                  </Button>
                )}
            </div>
          );
        })()}

        {/* Row 2: Description + firmographic one-liner + notes count */}
        <CustomerFirmographicSummary
          description={customer.description}
          ukTurnoverGbp={customer.uk_turnover_gbp}
          ukTurnoverAsOf={customer.uk_turnover_as_of}
          ukTurnoverConfidence={customer.uk_turnover_confidence}
          ukEmployees={customer.uk_employees}
          ukEmployeesAsOf={customer.uk_employees_as_of}
          ukEmployeesConfidence={customer.uk_employees_confidence}
          sector={customer.sector}
          website={customer.website}
          isEnriched={isEnriched}
          notesCount={notes.length}
          onOpenNotes={() =>
            navigate(`${paths.notes()}?customer=${customerId}`)
          }
          // Provenance for the figures on this very line, so it reads beside
          // them rather than competing with the customer's name.
          enrichmentStatus={
            <span className="inline-flex items-center gap-1.5">
              <span>
                {isEnriching
                  ? "Enriching…"
                  : customer.last_enriched_at
                    ? `Enriched ${formatDistanceToNow(
                        new Date(customer.last_enriched_at),
                        { addSuffix: true },
                      )}`
                    : hasEnrichment
                      ? "Enriched (date unknown)"
                      : "Not yet enriched"}
              </span>
              <button
                type="button"
                onClick={handleReEnrich}
                disabled={isEnriching}
                aria-label={
                  hasEnrichment ? "Re-enrich customer" : "Run enrichment"
                }
                className="text-muted-foreground/70 hover:text-foreground disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              >
                <RefreshCw
                  className={`size-4 ${isEnriching ? "animate-spin" : ""}`}
                />
              </button>
            </span>
          }
        />

        {/* Not truncated to a tooltip any more: an enrichment failure is the
            one message on this page a rep can act on, and the reason is the
            actionable part. */}
        {enrichError && (
          <p className="flex items-start gap-1.5 text-xs text-destructive">
            <AlertTriangle className="size-3.5 mt-px shrink-0" />
            <span>Enrichment failed: {enrichError}</span>
          </p>
        )}

        {/* Key decision maker(s) — rep-set flag, shown only when at least
            one contact is flagged (enhancement, not a required field). */}
        {keyPersonContacts.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Star className="size-3.5 fill-amber-400 text-amber-500" />
            <span className="text-muted-foreground">
              Key decision maker{keyPersonContacts.length > 1 ? "s" : ""}:
            </span>
            {keyPersonContacts.map((c) => (
              <Link
                key={c.id}
                to={paths.contactDetail({
                  id: c.id,
                  customerId: customer.customer.customer_code ?? undefined,
                })}
                className="font-medium text-foreground hover:underline"
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}

        {/* Row 4: the customer's tabs; this page is Overview. */}
        <CustomerTabs
          customerId={customer.customer.id}
          customerCode={customer.customer?.customer_code}
          customerName={customer.customer.name}
          cisCustomerCode={customer.customer.cis_customer_code}
          current="overview"
        />
      </div>

      {/* ── Billing group (Tharstern PostingAccount) ──────────────── */}
      <CustomerGroupCard
        group={customer.group}
        currentCustomerId={customerId}
      />

      {/* ── Improvement logs (unified Issues surface, customer-scoped) ── */}
      {customerId && <CustomerImprovementLogs customerId={customerId} />}

      {/* ── Portal people: a summary, managed in the portal's staff view ── */}
      {/* Portal people are keyed by the CIS customer id; the page's own id
          is its Tharstern (or CUS-) code. */}
      {canPreviewPortal && customer.customer.cis_customer_id && (
        <CustomerPortalSummary customerId={customer.customer.cis_customer_id} />
      )}

      {/* ── Pulse ──────────────────────────────────────────────────── */}
      <CustomerIdentityKpis
        customer={customer}
        liveEstimateCount={liveEstimateCount}
        liveEstimateValue={liveEstimateValue}
        liveJobCount={liveJobCount}
        liveJobValue={liveJobValue}
        daysSinceLastJob={customer.metrics?.days_since_last_order ?? null}
        nextMeetingAt={customer.next_meeting_at ?? null}
        openTicketCount={openTicketCount}
      />

      {/* ── Strategy pyramid: banner + 3 intent cards in tinted container ── */}
      <CustomerStrategyPyramid
        customer={customer}
        contacts={contacts}
        prospects={customer.prospect_contacts ?? []}
        pendingOpportunityCount={actions.length}
        openAlertCount={alerts.length}
        onSetHealthOverride={
          offersSetting("workflow.override_customer_health_tier")
            ? handleSetHealthOverride
            : undefined
        }
        onClearHealthOverride={
          offersSetting("workflow.end_customer_health_tier_override")
            ? handleClearHealthOverride
            : undefined
        }
      />

      {/* ── Campaign history: Mailchimp touches sent to this customer's contacts, who received them ── */}
      <CustomerCampaignHistory touches={customer.campaign_history ?? []} />

      {/* ── Contacts: everyone Tharstern holds for this customer, searchable/paginated ── */}
      <CustomerContacts
        customerId={customer.id}
        keyPersonContactIds={keyPersonContactIds}
        onToggleKeyPerson={
          offersSetting("workflow.set_customer_key_person")
            ? handleToggleKeyPerson
            : undefined
        }
      />

      {/* Create Ticket Modal */}
      {showCreateTicket && (
        <TicketModal
          onClose={() => setShowCreateTicket(false)}
          onSave={handleCreateTicket}
          isSubmitting={isSubmittingTicket}
        />
      )}
    </div>
  );
}
